import { AuctionSearchCriteriaInterface, AuctionTypeEnum, RangeInterface } from '@on-arte/core-types';
import { parse, ParsedQuery } from 'query-string';

/**
 * Filtry listingu dzieł — wspólne dla widoku i dla renderowania po stronie serwera.
 *
 * Odwzorowanie adresu na parametry pobrania MUSI być jedno. Gdy istniało tylko
 * w widoku, serwer zasiewał listing niefiltrowaną pierwszą stroną, więc wejście
 * na /dziela?type=buyNow (adres generowany przez samą stronę główną) oddawało
 * z serwera inny zestaw dzieł, niż wynikało z adresu.
 */

/**
 * Przedziały ceny i rozmiaru. Klucz to wartość widoczna w adresie (`?price=2500-5000`),
 * wartość to granice, którymi pyta API. Nazwy przedziałów są częścią adresów, więc
 * ich zmiana unieważnia zapisane i udostępnione odnośniki.
 */
export const rangeFilters: Record<'price' | 'size', Record<string, Partial<RangeInterface>>> = {
  price: {
    '1-2500': { from: 1, to: 2500 },
    '2500-5000': { from: 2500, to: 5000 },
    '5000-10000': { from: 5000, to: 10000 },
    'over-10000': { from: 10000 }
  },
  size: {
    'small': { to: 50 },
    'medium': { from: 50, to: 100 },
    'large': { from: 100 }
  }
};

/** Parametry adresu, które filtrami nie są: `id` wskazuje kafel do przewinięcia, `page` stronicuje. */
const nonFilterParams: string[] = ['id', 'page'];

/** Kryteria listingu przyjmujące tablicę tekstów. */
const textListCriteria: string[] = [
  'subjectValues',
  'manufacturerIds',
  'materialValues',
  'colorValues',
  'techniqueValues',
  'styleValues'
];

/** Kryteria listingu przyjmujące pojedynczą liczbę. */
const numberCriteria: string[] = ['minPrice', 'maxPrice', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight'];

/** Kryteria listingu przyjmujące wartość logiczną. */
const booleanCriteria: string[] = ['highlighted', 'hasActiveOffers'];

export type ArtworkListFilterValues = Record<string, number | string | string[]>;

/**
 * Wartości filtrów → parametry pobrania listingu.
 *
 * `price` i `size` to przedziały: w adresie stoi nazwa przedziału, a API pyta się
 * o granice, więc jedna wartość rozwija się w dwie (rozmiar w cztery, bo dotyczy
 * i wysokości, i szerokości). Pozostałe filtry idą wprost.
 *
 * Nieznana nazwa przedziału jest POMIJANA, nie rozwala pobrania. Wartość bierze się
 * z adresu, czyli od odwiedzającego, a na serwerze wyjątek stąd kosztowałby całą
 * stronę: znaczniki meta i dane strukturalne włącznie.
 */
export function mapArtworkListFilters(filtersValues: Record<string, string[]>): ArtworkListFilterValues {
  const result: ArtworkListFilterValues = {};

  Object.keys(filtersValues).forEach((key: string): void => {
    if (key !== 'price' && key !== 'size') {
      result[key] = filtersValues[key];

      return;
    }

    const range: Partial<RangeInterface> | undefined = rangeFilters[key][filtersValues[key][0]];
    if (!range) {
      return;
    }

    if (key === 'price') {
      if (range.from) {
        result.minPrice = range.from;
      }
      if (range.to) {
        result.maxPrice = range.to;
      }

      return;
    }

    if (range.from) {
      result.minHeight = range.from;
      result.minWidth = range.from;
    }
    if (range.to) {
      result.maxHeight = range.to;
      result.maxWidth = range.to;
    }
  });

  return result;
}

/**
 * Ciąg zapytania z adresu → wartości filtrów. Ta sama postać wejścia po obu stronach:
 * widok podaje `searchParams.toString()`, serwer wycinek adresu za znakiem zapytania.
 */
export function artworkListFilterValuesFromSearch(search: string): Record<string, string[]> {
  const parsed: ParsedQuery<string> = parse(search, { arrayFormat: 'separator', arrayFormatSeparator: ',' });
  const values: Record<string, string[]> = {};

  Object.entries(parsed).forEach(([key, value]: [string, string | (string | null)[] | null]): void => {
    if (nonFilterParams.includes(key)) {
      return;
    }

    if (typeof value === 'string') {
      values[key] = [value];

      return;
    }

    if (Array.isArray(value) && value.length) {
      values[key] = value.map((single: string | null): string => single ?? '');
    }
  });

  return values;
}

/**
 * Ciąg zapytania z adresu → kryteria, którymi serwer pyta `AuctionsService.getList`.
 *
 * Przeglądarka trafia w typy kryteriów okrężnie: składa parametry w ciąg zapytania,
 * a kontroler `/auctions` rozpakowuje je z powrotem i nadaje typy. Serwer woła usługę
 * WPROST, więc typy musi ustawić sam — inaczej `type` poszłoby jako tablica tam, gdzie
 * API czeka na pojedynczą wartość.
 *
 * Lista kryteriów jest zamknięta i pokrywa się z parametrami filtrującymi kontrolera.
 * To granica zaufania: adres pochodzi od odwiedzającego, a stąd wartości idą do API,
 * więc przepuszczamy wyłącznie to, co rozpoznajemy. `limit` i `offset` do niej nie należą,
 * bo o stronicowaniu decyduje zasiew, nie adres.
 */
export function artworkListCriteriaFromSearch(search: string): AuctionSearchCriteriaInterface {
  const filters: ArtworkListFilterValues = mapArtworkListFilters(artworkListFilterValuesFromSearch(search));
  const criteria: AuctionSearchCriteriaInterface = {};

  Object.entries(filters).forEach(([key, value]: [string, number | string | string[]]): void => {
    const list: string[] = Array.isArray(value) ? value : [String(value)];
    if (!list.length) {
      return;
    }

    if (textListCriteria.includes(key)) {
      Object.assign(criteria, { [key]: list });

      return;
    }

    if (key === 'categoryIds') {
      const numbers: number[] = list.map(Number).filter((single: number): boolean => !Number.isNaN(single));
      if (numbers.length) {
        criteria.categoryIds = numbers;
      }

      return;
    }

    if (numberCriteria.includes(key)) {
      const single: number = Number(list[0]);
      if (!Number.isNaN(single)) {
        Object.assign(criteria, { [key]: single });
      }

      return;
    }

    if (booleanCriteria.includes(key)) {
      Object.assign(criteria, { [key]: list[0] === 'true' });

      return;
    }

    if (key === 'type' && Object.values(AuctionTypeEnum).includes(list[0] as AuctionTypeEnum)) {
      criteria.type = list[0] as AuctionTypeEnum;
    }
  });

  return criteria;
}

/**
 * Stabilny odcisk kryteriów listingu — część klucza zasiewu z serwera.
 *
 * Bez niego zasiew leżał pod kluczem bez filtrów: po zmianie filtrów widok czyści
 * swoje wyniki, a wartość zapasowa oddawała komplet spod PIERWOTNEGO adresu. Użytkownik
 * widział wtedy prace spoza wybranego filtra, a przy nieudanym pobraniu zostawał przy
 * nich na stałe. Klucz z odciskiem po prostu nie trafia, więc zasiew przestaje być używany.
 *
 * Klucze i wartości tablicowe sortujemy, żeby ta sama treść filtrów dała ten sam odcisk
 * niezależnie od kolejności parametrów w adresie.
 */
export function artworkListCriteriaKey(criteria: AuctionSearchCriteriaInterface): string {
  const uporzadkowane: [string, unknown][] = Object.entries(criteria)
    .sort(([pierwszy]: [string, unknown], [drugi]: [string, unknown]): number => pierwszy.localeCompare(drugi))
    .map(([klucz, wartosc]: [string, unknown]): [string, unknown] => [
      klucz,
      Array.isArray(wartosc) ? [...wartosc as unknown[]].map(String).sort() : wartosc,
    ]);

  return JSON.stringify(uporzadkowane);
}
